import React, { useEffect, useState } from "react";
import { Phone, CalendarCheck, Share2, Mail, Link2, X } from "lucide-react";
import { getSettings } from "../../lib/api";
import { trackBooking } from "../../lib/tracking";
import { useLang } from "../../lib/i18n";
import { toast } from "sonner";

// Inline WhatsApp icon (no text per spec)
function WhatsAppIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
      <path d="M.057 24l1.687-6.163A11.867 11.867 0 0 1 .14 11.892C.142 5.335 5.48.001 12.05.001a11.82 11.82 0 0 1 8.413 3.488 11.821 11.821 0 0 1 3.48 8.414c-.003 6.557-5.342 11.892-11.892 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.51 5.26l.359.57-1.005 3.668 3.625-.956zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.414z" />
    </svg>
  );
}

export default function MobileBottomBar() {
  const [s, setS] = useState({});
  const [shareOpen, setShareOpen] = useState(false);
  const { lang, t } = useLang();
  useEffect(() => { getSettings(lang).then(setS).catch(() => {}); }, [lang]);

  const phone = (s.contact_phone || "").replace(/\s/g, "");
  const wa = (s.whatsapp_number || "").replace(/[^0-9+]/g, "");
  const terminUrl = s.doctolib_url || (lang === "en" ? "/en/kontakt/" : "/kontakt/");
  const terminExternal = !!s.doctolib_url;

  // Build share payload from the CURRENT page (title + SEO description + URL).
  const getShare = () => {
    const url = window.location.href;
    const title = (document.title || "Preventum Diez").replace(/\s*\|\s*Preventum Diez\s*$/i, "").trim() || "Preventum Diez";
    const desc = document.querySelector('meta[name="description"]')?.getAttribute("content") || "";
    const text = [title, desc, url].filter(Boolean).join("\n\n");
    return { url, title, desc, text };
  };
  const shareWhatsApp = () => { const { text } = getShare(); window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener"); setShareOpen(false); };
  const shareEmail = () => { const { title, desc, url } = getShare(); window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent([desc, url].filter(Boolean).join("\n\n"))}`; setShareOpen(false); };
  const copyLink = async () => {
    const { url } = getShare();
    try { await navigator.clipboard.writeText(url); toast.success(lang === "en" ? "Link copied" : "Link kopiert"); }
    catch { toast.error(lang === "en" ? "Could not copy" : "Kopieren fehlgeschlagen"); }
    setShareOpen(false);
  };

  const menuItem = "w-full flex items-center gap-3 px-4 py-3 text-sm text-navy-dark hover:bg-surface-muted text-left";

  return (
    <nav
      aria-label={lang === "en" ? "Mobile contact" : "Mobil-Kontakt"}
      className="md:hidden fixed bottom-0 inset-x-0 z-40"
      data-testid="mobile-bottom-bar"
    >
      {/* Share menu (appears above the bar) */}
      {shareOpen && (
        <>
          <div className="fixed inset-0 z-0" onClick={() => setShareOpen(false)} />
          <div className="relative z-10 mx-3 mb-2 rounded-2xl bg-white shadow-xl border border-outline overflow-hidden animate-fade-up" data-testid="share-menu">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-outline">
              <span className="text-xs font-semibold text-ink-muted uppercase tracking-wider">{lang === "en" ? "Recommend this page" : "Diese Seite empfehlen"}</span>
              <button onClick={() => setShareOpen(false)} aria-label="Schließen"><X className="h-4 w-4 text-ink-muted" /></button>
            </div>
            <button onClick={shareWhatsApp} className={menuItem} data-testid="share-whatsapp">
              <WhatsAppIcon className="h-5 w-5 text-[#25D366]" /> {lang === "en" ? "Share via WhatsApp" : "Per WhatsApp teilen"}
            </button>
            <button onClick={shareEmail} className={menuItem} data-testid="share-email">
              <Mail className="h-5 w-5 text-navy" /> {lang === "en" ? "Share via Email" : "Per E-Mail teilen"}
            </button>
            <button onClick={copyLink} className={menuItem} data-testid="share-copy">
              <Link2 className="h-5 w-5 text-ink-muted" /> {lang === "en" ? "Copy link" : "Link kopieren"}
            </button>
          </div>
        </>
      )}

      <div className="px-3 pb-3 pt-2" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
        <div className="neu-card flex items-center gap-1.5" style={{ borderRadius: "9999px", padding: "0.4rem 0.5rem" }}>
          <a
            href={terminUrl}
            {...(terminExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            onClick={() => trackBooking("mobile_footer")}
            data-testid="mbb-termin"
            className="flex-1 min-w-0 h-11 flex items-center justify-center gap-1.5 rounded-full bg-brandred text-white font-semibold text-[13px] active:scale-95 transition px-3"
          >
            <CalendarCheck className="h-4 w-4 shrink-0" />
            <span className="truncate">{lang === "en" ? "Appointment" : "Termin"}</span>
          </a>
          <button
            onClick={() => setShareOpen((o) => !o)}
            data-testid="mbb-empfehlen"
            className="h-11 flex items-center gap-1.5 rounded-full border border-outline text-navy-dark font-semibold text-[13px] active:scale-95 transition px-3"
          >
            <Share2 className="h-4 w-4 shrink-0" />
            <span>{lang === "en" ? "Share" : "Empfehlen"}</span>
          </button>
          {phone && (
            <a
              href={`tel:${phone}`}
              aria-label={t("mbCall")}
              data-testid="mbb-phone"
              className="h-11 w-11 shrink-0 flex items-center justify-center rounded-full text-navy hover:text-brandred active:scale-95 transition"
            >
              <Phone className="h-5 w-5" />
            </a>
          )}
          {wa && (
            <a
              href={`https://wa.me/${wa.replace(/^\+/, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t("mbWhatsApp")}
              data-testid="mbb-whatsapp"
              className="h-11 w-11 shrink-0 flex items-center justify-center rounded-full text-[#25D366] hover:opacity-80 active:scale-95 transition"
            >
              <WhatsAppIcon className="h-6 w-6" />
            </a>
          )}
        </div>
      </div>
    </nav>
  );
}
